import ProductModel from "@/models/server/ProductModel";
import TagManager from "react-gtm-module";
import RentaEasyConstants from "@/helpers/RentaEasyConstants";
import ShoppingCartProductModel from "@/models/server/ShoppingCartProductModel";

export const GtmEvents = {
    VIEW_CART: "view_cart",
    ADD_TO_CART: "add_to_cart",
    PURCHASE: "purchase"
};
export default class TagManagerHelper {

    public static validateGtmEvent(event: string, checkLastOnly: boolean = true): void {
        cy.window().then((window) => {
            assert.isDefined(window.dataLayer);
            console.log(window.dataLayer);

            if (checkLastOnly) {
                const lastEvent = window.dataLayer[window.dataLayer.length - 1];
                assert.equal(lastEvent.event, event, `Expected ${event} event to be the last element in dataLayer`);
            } else {
                const eventExists = window.dataLayer.some(e => e.event === event);
                assert.isTrue(eventExists, `Expected ${event} event to exist in dataLayer`);
            }
        });
    }

    static pushPurchaseEvent(shoppingCartProductModels: ShoppingCartProductModel[], orderId: string | null): void {
        if (!shoppingCartProductModels.length || !orderId) return; // Ensure we have data before pushing

        // Filter out items where `product` is null
        const validProducts = shoppingCartProductModels.filter(item => item.product !== null) as {
            product: ProductModel;
            count: number;
        }[];

        if (validProducts.length === 0) return; // Avoid sending an empty event

        // Calculate total value and tax
        const totalValue = validProducts.reduce((sum, item) => sum + ((item.product.dailyPrice ?? 0) * item.count), 0);
        const totalTax = validProducts.reduce((sum, item) =>
            sum + (((item.product.dailyPriceWithTax ?? 0) - (item.product.dailyPrice ?? 0)) * item.count), 0
        );

        TagManager.dataLayer({
            dataLayer: {
                event: GtmEvents.PURCHASE,
                ecommerce: {
                    transaction_id: orderId,
                    affiliation: window.location.href,
                    currency: RentaEasyConstants.environmentCurrencyCode,
                    value: totalValue,
                    tax: totalTax,
                    shipping: 0,
                    items: TagManagerHelper.getGtmProductItems(validProducts)
                }
            },
        });
    }

    static pushViewCartEvent(shoppingCartProductModels: ShoppingCartProductModel[]): void {
        if (!shoppingCartProductModels.length) return; // Avoid sending an empty event

        // Filter out items where `product` is null
        const validProducts = shoppingCartProductModels.filter(item => item.product !== null) as {
            product: ProductModel;
            count: number;
        }[];

        if (validProducts.length === 0) return; // Avoid sending an empty event

        const totalValue = validProducts.reduce((sum, item) =>
            sum + ((item.product.dailyPrice ?? 0) * item.count), 0
        );

        TagManager.dataLayer({
            dataLayer: {
                event: GtmEvents.VIEW_CART,
                ecommerce: {
                    currency: RentaEasyConstants.environmentCurrencyCode,
                    value: totalValue,
                    items: TagManagerHelper.getGtmProductItems(validProducts)
                }
            },
        });
    }

    static pushAddToCartEvent(productModel: ProductModel, count: number): void {
        TagManager.dataLayer({
            dataLayer: {
                event: GtmEvents.ADD_TO_CART,
                ecommerce: {
                    currency: RentaEasyConstants.environmentCurrencyCode,
                    value: (productModel.dailyPrice ?? 0) * count,
                    items: TagManagerHelper.getGtmProductItems([{ product: productModel, count }])
                }
            },
        });
    }

    /**
     * Generates GTM items array for products, used in add_to_cart and view_cart events.
     */
    private static getGtmProductItems(products: { product: ProductModel; count: number }[]): Record<string, any>[] {
        return products.map(item => {
            const product = item.product;
            const categoryHierarchy = TagManagerHelper.getCategoryHierarchy(product);

            return {
                item_id: product.id,
                item_name: product.name,
                price: product.dailyPrice,
                quantity: item.count,
                ...categoryHierarchy
            };
        });
    }

    /**
     * Extracts category hierarchy and formats it for GTM.
     */
    private static getCategoryHierarchy(productModel: ProductModel): Record<string, string | undefined> {
        let categoryLevels: string[] = [];
        let currentCategory = productModel.category;

        while (currentCategory) {
            categoryLevels.unshift(currentCategory.name);
            currentCategory = currentCategory.parent;
        }

        return categoryLevels.reduce((acc, category, index) => {
            acc[index === 0 ? "category" : `category${index}`] = category;
            return acc;
        }, {} as Record<string, string | undefined>);
    }
}